import { t } from "@lingui/macro";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { format, isValid } from "date-fns";
import { upperCase } from "lodash";
import { useFragment } from "react-relay";

import {
  lastSeriesOccurrenceDate,
  getMaximumOccurrenceCount,
} from "@/features/RecurringRequests/recurrence";
import {
  RecurrenceAvailabilityPreview,
  RecurrenceFormValue,
} from "@/features/RecurringRequests/types";
import {
  Text,
  Box,
  HStack,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import {
  convertAgendaDateTimeToDate,
  convertDateStringToObj,
  formatHHMM,
} from "@whoosh/utilities/src/dates";

import { WeatherCondition } from "./WeatherCondition";
import { BookingInfo_Booking$key } from "./__generated__/BookingInfo_Booking.graphql";
import { isActivePatronRequestSeries } from "./isActivePatronRequestSeries";
import { recurringSeriesHeaderSubtitle } from "./recurringSeriesHeader";

type Props = {
  bookingRef: BookingInfo_Booking$key | null;
  children?: React.ReactNode;
  forceRequestState?: boolean;
  hideStartTime?: boolean;
  recurrenceAvailabilityPreview?: RecurrenceAvailabilityPreview | null;
  pendingRecurrence?: RecurrenceFormValue | null;
};

export const BookingInfo = ({
  bookingRef,
  children,
  forceRequestState = false,
  hideStartTime = false,
  recurrenceAvailabilityPreview = null,
  pendingRecurrence = null,
}: Props) => {
  const data = useFragment(BookingInfoFragmentString, bookingRef);
  const isMobile = useResponsiveProp({ base: 1, md: 0 });
  const parsedSlotDateTime = data?.startSlot?.dateTime
    ? convertAgendaDateTimeToDate(data.startSlot.dateTime)
    : null;
  const slotDateTime =
    parsedSlotDateTime && isValid(parsedSlotDateTime)
      ? parsedSlotDateTime
      : null;
  const formattedTime = hideStartTime
    ? undefined
    : slotDateTime
    ? format(slotDateTime, "h:mm aaa")
    : formatHHMM(data?.startSlot?.time || "") ?? undefined;
  const dateObj = slotDateTime
    ? slotDateTime
    : data?.startSlot?.agenda?.date
    ? convertDateStringToObj(data?.startSlot?.agenda?.date)
    : new Date();
  const formattedDate = format(dateObj, "EEE, MMM do");

  const title = `${formattedDate}${formattedTime ? `, ${formattedTime}` : ""} `;

  const facilityType =
    data?.startSlot?.agenda?.facility?.type === "GOLF_COURSE" ? "GOLF" : "";
  const facilityName = data?.startSlot?.agenda?.facility?.name ?? "RESTAURANT";

  const isQueueSlot =
    forceRequestState || data?.startSlot?.__typename === "QueueSlot";
  const isDiningBooking = data?.__typename === "DiningBooking";
  const diningBookingEyebrowTitle = t`Dining Reservation: ${facilityName}`;

  const sourceDate = data?.startSlot?.agenda?.date ?? null;
  const series = data?.bookingSeries;
  const seriesBookings =
    series?.bookings?.edges
      ?.map((edge) => edge?.node)
      .filter(
        (seriesBooking): seriesBooking is NonNullable<typeof seriesBooking> =>
          !!seriesBooking
      ) ?? [];
  const sortedSeriesDates = seriesBookings
    .map((seriesBooking) => seriesBooking.startSlot?.agenda?.date)
    .filter((date): date is string => !!date)
    .sort();
  const hasPersistedSeries = isActivePatronRequestSeries({
    hasSeriesRecurrence: !!series?.recurrenceType,
    remainingBookingCount: sortedSeriesDates.length,
  });
  const isRecurringRequest =
    isQueueSlot && (hasPersistedSeries || !!pendingRecurrence);
  const seriesSubtitle = (() => {
    if (!isRecurringRequest || !sourceDate) return null;

    if (hasPersistedSeries && series) {
      const positionIndex = Math.max(
        sortedSeriesDates.findIndex((date) => date === sourceDate),
        0
      );
      return recurringSeriesHeaderSubtitle({
        positionIndex,
        totalCount: sortedSeriesDates.length,
        recurrenceType: series.recurrenceType,
        repeatInterval: series.repeatInterval ?? 1,
        // The selected occurrence is the anchor for the edited segment, so describe
        // the cadence from the request the patron is currently viewing.
        sourceDate,
        endsDate:
          series.endCondition === "ON_DATE"
            ? series.effectiveEndDate
            : sortedSeriesDates.at(-1),
      });
    }

    if (!pendingRecurrence) return null;

    const previewDates = recurrenceAvailabilityPreview?.included
      .map((occurrence) => occurrence.date)
      .sort();
    const totalCount = recurrenceAvailabilityPreview
      ? recurrenceAvailabilityPreview.included.length
      : pendingRecurrence.endCondition === "AFTER_OCCURRENCES"
      ? pendingRecurrence.occurrenceCount ?? 1
      : pendingRecurrence.endDate
      ? getMaximumOccurrenceCount({
          sourceDate,
          maximumEndDate: pendingRecurrence.endDate,
          recurrenceType: pendingRecurrence.recurrenceType,
          repeatInterval: pendingRecurrence.repeatInterval,
        })
      : 1;
    const endsDate = recurrenceAvailabilityPreview
      ? previewDates?.at(-1)
      : pendingRecurrence.endCondition === "ON_DATE"
      ? pendingRecurrence.endDate
      : lastSeriesOccurrenceDate({
          sourceDate,
          recurrenceType: pendingRecurrence.recurrenceType,
          repeatInterval: pendingRecurrence.repeatInterval,
          occurrenceCount: totalCount,
        });

    return recurringSeriesHeaderSubtitle({
      positionIndex: 0,
      totalCount,
      recurrenceType: pendingRecurrence.recurrenceType,
      repeatInterval: pendingRecurrence.repeatInterval,
      sourceDate,
      endsDate,
    });
  })();

  const eyebrowTitle = isRecurringRequest
    ? facilityType
      ? t`RECURRING ${facilityType} REQUEST`
      : t`RECURRING REQUEST`
    : t`${facilityType} ${
        isDiningBooking
          ? diningBookingEyebrowTitle.toUpperCase()
          : isQueueSlot
          ? "REQUEST"
          : "BOOKING"
      }`;

  return (
    <>
      <Box padding={5} gap={2} backgroundColor="surface-primary">
        <HStack gap={1} alignItems="center">
          <Text color="text-secondary" variant="mainTiny">
            {eyebrowTitle}
          </Text>
          {data?.startSlot?.course?.name ? (
            <>
              <Text color="text-secondary" variant="mainTiny">
                ·
              </Text>
              <Text color="text-secondary" variant="mainTiny">{t`${upperCase(
                data?.startSlot?.course?.name
              )}`}</Text>
            </>
          ) : null}
        </HStack>
        <VStack gap={5}>
          <HStack
            justifyContent="space-between"
            alignItems={isMobile ? "flex-start" : "center"}
            flexDirection={isMobile ? "column" : "row"}
            gap={isMobile ? 3 : 0}
          >
            <VStack gap={1} flex={1}>
              <Text color="text-primary" variant="mainTitle3">
                {title}
              </Text>
              {seriesSubtitle ? (
                <Text color="text-secondary" variant="mainBaseMedium">
                  {seriesSubtitle}
                </Text>
              ) : null}
            </VStack>
            <WeatherCondition
              weatherCondition={data?.startSlot?.weatherCondition}
            />
          </HStack>
        </VStack>
        {children}
      </Box>
    </>
  );
};

const BookingInfoFragmentString = graphql`
  fragment BookingInfo_Booking on Booking {
    id
    __typename
    startSlot {
      __typename
      ... on BucketTimeSlot {
        time
      }
      ... on TimeSlot {
        time
        dateTime
        weatherCondition {
          temperature
          weatherCategory
          measurementSystem
          windSpeedInMph
        }
        course {
          name
        }
      }
      agenda {
        date
        facility {
          type
          name
        }
      }
    }
    bookingSeries {
      recurrenceType
      repeatInterval
      endCondition
      effectiveEndDate
      requestedOccurrenceCount
      bookings(first: 366, orderBy: [{ field: DATE_TIME, direction: DESC }]) {
        edges {
          node {
            id
            startSlot {
              agenda {
                date
              }
            }
          }
        }
      }
    }
  }
`;
